<template>
    <div class="ui-segmented" v-bind:class="`ui-segmented--${variant}`" role="radiogroup" ref="tray">
        <!-- One raised pill (or underline) glides between options; the options sit on top of it: -->
        <span v-if="variant === 'thumb' || variant === 'underline'" class="ui-segmented__thumb" v-bind:style="thumbStyle"/>

        <button type="button" class="ui-segmented__option" role="radio"
            v-for="option in options"
            v-bind:key="option.value"
            ref="optionEls"
            v-bind:data-value="option.value"
            v-bind:aria-checked="option.value === value"
            v-bind:class="{ 'ui-segmented__option--active': option.value === value }"
            v-on:click="$emit('input', option.value)">
            <component v-if="option.icon" v-bind:is="option.icon" class="ui-segmented__icon"/>
            <slot name="icon" v-bind:option="option"/>
            <span class="ui-segmented__label">{{option.label}}</span>
            <icon-check v-if="variant === 'list'" class="ui-segmented__check"/>
        </button>
    </div>
</template>

<script>
import IconCheck from '@img/icons/lucide/check.svg?vue';

/**
 * Segmented control. Options: [{ value, label, icon? }]. Works with v-model.
 * Variants: 'thumb' (tray with a sliding pill), 'chips' (separate outlined pills),
 * 'tiles' (icon above label), 'list' (rows with a check), 'underline' (text with a sliding line).
 */
export default {
    props: {
        value: [String, Number],
        options: {
            type: Array,
            required: true,
        },
        variant: {
            type: String,
            default: 'thumb',
        },
    },

    components: {
        IconCheck,
    },

    data() {
        return {
            thumbStyle: { opacity: 0 },
            thumbReady: false,
            resizeObserver: undefined,
        };
    },

    watch: {
        value: 'updateThumb',
        options: 'updateThumb',
    },

    mounted() {
        this.updateThumb();

        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.updateThumb());
            this.resizeObserver.observe(this.$refs.tray);
        } else if (document.fonts?.ready) {
            document.fonts.ready.then(this.updateThumb);
        }
    },

    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },

    methods: {
        updateThumb() {
            this.$nextTick(() => {
                const els = this.$refs.optionEls || [];
                const activeEl = els.find(el => el && el.dataset.value === String(this.value));

                if (!activeEl) {
                    this.thumbStyle = { opacity: 0 };
                    return;
                }

                this.thumbStyle = {
                    opacity: 1,
                    transform: `translateX(${activeEl.offsetLeft}px)`,
                    width: `${activeEl.offsetWidth}px`,
                    // The first placement is applied without a transition, otherwise
                    // the pill would visibly fly in from the left on mount:
                    transition: this.thumbReady ? undefined : 'none',
                };

                if (!this.thumbReady) {
                    requestAnimationFrame(() => {
                        this.thumbReady = true;
                    });
                }
            });
        },
    },
};
</script>

<style lang="scss">
.ui-segmented {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    border-radius: 11px;
    background: var(--body-light-muted-color);
    box-sizing: border-box;
    max-width: 100%;

    &__thumb {
        position: absolute;
        top: 3px;
        left: 0;
        z-index: 0;
        height: 32px;
        border-radius: 8px;
        background: var(--tab-thumb-background);
        box-shadow: var(--tab-thumb-shadow);
        pointer-events: none;
        transition: transform .3s cubic-bezier(.22, 1, .36, 1),
                    width .3s cubic-bezier(.22, 1, .36, 1),
                    opacity .2s ease;
    }

    &__option {
        position: relative;
        z-index: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        height: 32px;
        padding: 0 13px;
        border: none;
        border-radius: 8px;
        background: transparent;
        font-family: inherit;
        font-size: 13px;
        font-weight: 500;
        letter-spacing: -0.01em;
        color: var(--body-muted-text-color);
        white-space: nowrap;
        cursor: pointer;
        user-select: none;
        transition: color .15s ease, transform .1s ease;

        &:hover {
            color: var(--body-text-color);
        }
        &:active {
            transform: scale(.96);
        }
        &:focus-visible {
            outline: 2px solid var(--blue-bright);
            outline-offset: -2px;
        }

        &--active {
            color: var(--body-text-color);
            .ui-segmented__icon {
                color: var(--blue-bright);
            }
        }
    }

    &__icon {
        width: 15px;
        height: 15px;
        flex-shrink: 0;
        transition: color .15s ease;
    }

    &__check {
        display: none;
    }

    &--stretch {
        display: flex;
        .ui-segmented__option {
            flex: 1;
        }
    }

    // ---- Variant: separate outlined chips, no tray ----
    &--chips {
        gap: 6px;
        padding: 0;
        background: transparent;
        border-radius: 0;
        .ui-segmented__option {
            height: 34px;
            border: 1px solid var(--card-border-color);
            border-radius: 999px;
            transition: color .15s ease, border-color .15s ease, background-color .15s ease, transform .1s ease;
            // Hover only fills the chip softly; the border stays put so nothing jumps:
            &:hover {
                color: var(--body-text-color);
                background: var(--icon-button-hover-background);
            }
            &--active {
                &, &:hover {
                    color: var(--blue-bright);
                    border-color: var(--blue-bright);
                    background: var(--blue-bright-background);
                }
            }
        }
    }

    // ---- Variant: tiles with the icon above the label ----
    &--tiles {
        gap: 8px;
        padding: 0;
        background: transparent;
        border-radius: 0;
        .ui-segmented__option {
            flex-direction: column;
            gap: 6px;
            height: auto;
            padding: 10px 12px 9px;
            border-radius: 12px;
            border: 1px solid var(--card-border-color);
            background: var(--card-background);
            font-size: 12.5px;
            transition: color .15s ease, border-color .15s ease, background-color .15s ease, transform .1s ease;
            &:hover {
                border-color: var(--body-muted-text-color);
            }
            &--active {
                color: var(--body-text-color);
                border-color: var(--blue-bright);
                background: var(--blue-bright-background);
            }
        }
        .ui-segmented__icon {
            width: 20px;
            height: 20px;
        }
    }

    // ---- Variant: a list of rows with a check on the chosen one ----
    &--list {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 0;
        padding: 0;
        background: transparent;
        border: 1px solid var(--card-border-color);
        border-radius: 12px;
        overflow: hidden;
        .ui-segmented__option {
            flex: none !important; // rows keep their height even where the parent stretches options
            justify-content: flex-start;
            height: 40px;
            padding: 0 12px 0 14px;
            border-radius: 0;
            & + .ui-segmented__option {
                border-top: 1px solid var(--card-row-separator);
            }
            &:hover {
                background: var(--icon-button-hover-background);
            }
            &:active {
                transform: none;
            }
        }
        .ui-segmented__label {
            flex: 1;
            text-align: left;
        }
        .ui-segmented__check {
            display: block;
            width: 16px;
            height: 16px;
            color: var(--blue-bright);
            opacity: 0;
            transform: scale(.6);
            transition: opacity .15s ease, transform .2s cubic-bezier(.2, .8, .2, 1);
        }
        .ui-segmented__option--active .ui-segmented__check {
            opacity: 1;
            transform: none;
        }
    }

    // ---- Variant: plain text with a line that slides underneath ----
    &--underline {
        gap: 18px;
        padding: 0;
        background: transparent;
        border-radius: 0;
        .ui-segmented__thumb {
            top: auto;
            bottom: 0;
            height: 2px;
            border-radius: 2px;
            background: var(--blue-bright);
            box-shadow: none;
        }
        .ui-segmented__option {
            padding: 0 2px;
            border-radius: 0;
            &:hover {
                background: transparent;
            }
            &:active {
                transform: none;
            }
            &--active {
                color: var(--blue-bright);
            }
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .ui-segmented__thumb,
    .ui-segmented__option {
        transition: none;
    }
}
</style>
